import { components as c } from "@/components";
import {
    searchForTemplateAiWizardNavigation,
    useTemplateAiWizardSearch,
} from "@/features/ai-builder/useTemplateAiDraftStore";
import { SurgeryCliniciansFormFields } from "@/features/events/features/surgeries/components/SurgeryCliniciansFormFields";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useTemplateLocks } from "@/features/templates/hooks/useTemplateLocks";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { templateSettingsSchema } from "@patient-watch/types";
import { isUuid } from "@/services/isUuid";
import { useAuth } from "@/utils/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const surgeryFormSchema = z.object({
    referring_clinician_id: z.string().uuid().optional().nullable(),
    operating_clinician_id: z.string().uuid().optional().nullable(),
});

type SurgeryFormValues = z.infer<typeof surgeryFormSchema>;

const SurgeryFormFieldsPage = () => {
    const auth = useAuth();
    const { templateId } = Route.useParams();
    const locks = useTemplateLocks(templateId);
    const referringLocked = locks.isLocked("referring_clinician_id");
    const operatingLocked = locks.isLocked("operating_clinician_id");
    const search = useTemplateAiWizardSearch(templateId, Route.useSearch());
    const organisationId = search.organisation_id ?? undefined;
    const referring_clinician_id = search.referring_clinician_id;
    const operating_clinician_id = search.operating_clinician_id;

    const { markStepComplete, nextStep, setCurrentStep, getNextStepRoute, getPreviousStepRoute, currentStep } =
        useTemplateNavigationStore();
    const navigate = useNavigate();

    const form = useForm<SurgeryFormValues>({
        resolver: zodResolver(surgeryFormSchema),
        defaultValues: {
            referring_clinician_id: "",
            operating_clinician_id: "",
        },
    });

    useEffect(() => {
        setCurrentStep("surgery");
    }, [setCurrentStep]);

    // Set form values after options load - using reset to ensure ComboBox recognizes values
    useEffect(() => {
        const referringValue = isUuid(referring_clinician_id)
            ? referring_clinician_id
            : referringLocked
              ? ""
              : auth?.id || "";
        const operatingValue = isUuid(operating_clinician_id)
            ? operating_clinician_id
            : operatingLocked
              ? ""
              : auth?.id || "";

        form.reset({
            referring_clinician_id: referringValue,
            operating_clinician_id: operatingValue,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [referring_clinician_id, operating_clinician_id, auth?.id, referringLocked, operatingLocked]);

    const handleNext = (formData: SurgeryFormValues) => {
        markStepComplete(currentStep);
        nextStep();
        const nextRoute = getNextStepRoute(currentStep, { templateId });
        if (nextRoute) {
            const nextSettings = { ...search, ...formData };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            void navigate({
                ...nextRoute,
                search: wizardSearchPayload,
            });
        }
    };

    const handlePrevious = () => {
        const prevRoute = getPreviousStepRoute(currentStep, { templateId });
        if (prevRoute) {
            const nextSettings = { ...search, ...form.getValues() };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            void navigate({
                ...prevRoute,
                search: wizardSearchPayload,
            });
        }
    };

    return (
        <c.ui.Form {...form}>
            <form onSubmit={form.handleSubmit(handleNext)} className="space-y-6">
                <SurgeryCliniciansFormFields organisationId={organisationId} />

                <TemplateStepFooter onBack={handlePrevious} submitPrimary />
            </form>
        </c.ui.Form>
    );
};

const surgerySearchSchema = templateSettingsSchema.partial().pick({
    organisation_id: true,
    referring_clinician_id: true,
    operating_clinician_id: true,
});

export const Route = createFileRoute("/_app/templates/$templateId/events/surgery/")({
    component: SurgeryFormFieldsPage,
    validateSearch: (search) => surgerySearchSchema.parse(search),
});
